import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMusicPlayer } from '../MusicPlayerContext';
import { getCoverImageUrl, hasVideo } from '../utils/musicStreamUrls';
import { isInstrumentalMusic } from '../utils/musicMeta';
import { useUser } from '../UserContext';
import { AdminContext } from '../AdminContext';
import { getFreeMemberNewSongInfo } from '../utils/playbackGate';
import { showUiHint } from '../utils/uiHint';
import { spaNavigate } from '../utils/spaNavigate';
import { theme, buttonClass } from '../ui/theme';

const MusicRankings = () => {
  const { i18n, t } = useTranslation();
  const { setCurrentMusic, addToPlaylist, setPlaying } = useMusicPlayer();
  const { user } = useUser();
  const { isAdmin } = useContext(AdminContext);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [type, setType] = useState('play'); // play, favorite, download, new
  const [period, setPeriod] = useState('all'); // all, today, week, month, year
  const [stats, setStats] = useState(null);
  const btnOutlineSm = buttonClass({ variant: 'outline', size: 'sm' });

  const fetchRankings = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get('/api/music/rankings', {
        params: {
          type,
          period,
          limit: 50
        }
      });
      
      if (response.data.success) {
        setRankings(response.data.data.rankings);
      } else {
        setError(response.data.error || t('musicRankings.fetchFailed'));
      }
    } catch (err) {
      console.error('Fetch rankings error:', err);
      setError(err.response?.data?.error || t('musicRankings.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [type, period, t]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/music/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  }, []);

  useEffect(() => {
    fetchRankings();
    fetchStats();
  }, [fetchRankings, fetchStats]);

  const handleMusicSelect = (music) => {
    const gate = getFreeMemberNewSongInfo({ music, user, isAdmin: !!isAdmin });
    if (gate?.blocked) {
      showUiHint(t('musicList.freeMemberNewSongHint', { days: Number(gate?.daysLeft || 0) }), { duration: 2200 });
      return;
    }
    setCurrentMusic(music);
    setPlaying(true);
    addToPlaylist(music);
  };

  const handlePlayAll = useCallback(() => {
    if (!rankings || rankings.length === 0) return;
    const playable = [];
    let firstBlocked = null;
    rankings.forEach((music) => {
      const gate = getFreeMemberNewSongInfo({ music, user, isAdmin: !!isAdmin });
      if (gate?.blocked) {
        if (!firstBlocked) firstBlocked = gate;
        return;
      }
      playable.push(music);
    });
    if (playable.length === 0) {
      if (firstBlocked) {
        showUiHint(t('musicList.freeMemberNewSongHint', { days: Number(firstBlocked?.daysLeft || 0) }), { duration: 2200 });
      }
      return;
    }
    playable.forEach((music) => addToPlaylist(music));
    setCurrentMusic(playable[0]);
    setPlaying(true);
  }, [rankings, user, isAdmin, t, addToPlaylist, setCurrentMusic, setPlaying]);

  // 统一：跳转到静态化详情页（.html）
  const handleOpenDetail = useCallback((music, e) => {
    try { e?.stopPropagation?.(); } catch (_) {}
    if (!music || !music.id) return;
    const currentLang = i18n?.language || 'ja';
    spaNavigate(`/${currentLang}/music/${music.id}.html`);
  }, [i18n?.language]);

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentLanguageTitle = (music) => {
    const lang = i18n.language || 'cn';
    switch (lang) {
      case 'cn':
        return music.title_zh_cn || music.title;
      case 'tw':
        return music.title_zh_tw || music.title;
      case 'ja':
        return music.title_ja || music.title;
      case 'en':
        return music.title_en || music.title;
      default:
        return music.title;
    }
  };

  const getRankingTitle = () => {
    const typeMap = {
      play: t('musicRankings.playRanking'),
      favorite: t('musicRankings.favoriteRanking'),
      download: t('musicRankings.downloadRanking'),
      new: t('musicRankings.newUploads')
    };
    const periodMap = {
      all: t('musicRankings.periodAll'),
      today: t('musicRankings.periodToday'),
      week: t('musicRankings.periodWeek'),
      month: t('musicRankings.periodMonth'),
      year: t('musicRankings.periodYear')
    };
    return t('musicRankings.rankingTitle', { period: periodMap[period], type: typeMap[type] });
  };

  if (loading && !rankings.length) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-white/70">{t('musicRankings.loading')}</p>
      </div>
    );
  }

  if (error && !rankings.length) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchRankings}
          className={['mt-4', btnOutlineSm].join(' ')}
        >
          {t('musicRankings.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 统计信息 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-3 text-center'].join(' ')}>
            <p className="text-lg font-bold text-white">{stats.total}</p>
            <p className="text-xs text-white/60 mt-0.5">{t('musicRankings.totalMusic')}</p>
          </div>
          <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-3 text-center'].join(' ')}>
            <p className="text-lg font-bold text-white">{stats.totalPlayCount}</p>
            <p className="text-xs text-white/60 mt-0.5">{t('musicRankings.totalPlayCount')}</p>
          </div>
          <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-3 text-center'].join(' ')}>
            <p className="text-lg font-bold text-white">{stats.totalFavorites}</p>
            <p className="text-xs text-white/60 mt-0.5">{t('musicRankings.totalFavorites')}</p>
          </div>
          <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-3 text-center'].join(' ')}>
            <p className="text-lg font-bold text-white">{stats.recentUploads}</p>
            <p className="text-xs text-white/60 mt-0.5">{t('musicRankings.recentUploads')}</p>
          </div>
        </div>
      )}

      {/* 排行榜控制 */}
      <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-3'].join(' ')}>
        <div className="flex flex-wrap gap-3 items-center">
          <h2 className="text-lg font-semibold text-white">{getRankingTitle()}</h2>
          
          <div className="flex gap-1.5 items-center">
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className={['px-1.5 py-0.5 text-xs', theme.inputs.base].join(' ')}
            >
              <option value="play">{t('musicRankings.playRanking')}</option>
              <option value="favorite">{t('musicRankings.favoriteRanking')}</option>
              <option value="download">{t('musicRankings.downloadRanking')}</option>
              <option value="new">{t('musicRankings.newUploads')}</option>
            </select>
            
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className={['px-1.5 py-0.5 text-xs', theme.inputs.base].join(' ')}
            >
              <option value="all">{t('musicRankings.periodAll')}</option>
              <option value="today">{t('musicRankings.periodToday')}</option>
              <option value="week">{t('musicRankings.periodWeek')}</option>
              <option value="month">{t('musicRankings.periodMonth')}</option>
              <option value="year">{t('musicRankings.periodYear')}</option>
            </select>
            <button
              type="button"
              className={[btnOutlineSm, 'whitespace-nowrap'].join(' ')}
              onClick={handlePlayAll}
              disabled={loading || !!error || rankings.length === 0}
            >
              {t('musicList.playAll', '播放全部')}
            </button>
          </div>
        </div>
      </div>

      {/* 排行榜列表 */}
      <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg overflow-hidden'].join(' ')}>
        {rankings.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-white/70">{t('musicRankings.noData')}</p>
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {rankings.map((music, index) => (
              <div
                key={music.id}
                onClick={() => handleMusicSelect(music)}
                className="p-3 hover:bg-white/[0.04] cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* 排名 */}
                  <div className="flex-shrink-0 w-6 text-center">
                    {index < 3 ? (
                      <span className={`text-lg font-bold ${
                        index === 0 ? 'text-yellow-500' :
                        index === 1 ? 'text-gray-400' :
                        'text-orange-500'
                      }`}>
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                      </span>
                    ) : (
                      <span className="text-sm font-semibold text-white/55">
                        {index + 1}
                      </span>
                    )}
                  </div>

                  {/* 封面 */}
                  <div className="flex-shrink-0 relative">
                    {getCoverImageUrl(music) ? (
                      <img
                        src={getCoverImageUrl(music)}
                        alt={getCurrentLanguageTitle(music)}
                        className="w-12 h-12 rounded object-cover"
                      />
                    ) : (
                      <div className={['w-12 h-12 rounded flex items-center justify-center', theme.surfaces.glassItem].join(' ')}>
                        <svg className="w-6 h-6 text-white/45" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                        </svg>
                      </div>
                    )}
                    {/* MTV 图标（如果是视频） */}
                    {hasVideo(music) && (
                      <img
                        src="/icons/mtv.png"
                        alt="MTV"
                        className="absolute top-0 right-0 w-4 h-4 object-contain"
                      />
                    )}
                    {/* 纯音乐图标 */}
                    {!hasVideo(music) && isInstrumentalMusic(music) && (
                      <img
                        src="/icons/mcd.png"
                        alt={t('musicList.categoryInstrumental', '纯音乐')}
                        title={t('musicList.categoryInstrumental', '纯音乐')}
                        className="absolute top-0 right-0 w-4 h-4 object-contain"
                      />
                    )}
                  </div>

                  {/* 信息 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate flex-1 min-w-0 inline-flex items-center gap-1">
                        {hasVideo(music) && (
                          <img
                            src="/icons/mtv-s.png"
                            alt="MTV"
                            className="w-4 h-4 flex-shrink-0 object-contain"
                            title={t('player.hasVideo') || '视频'}
                          />
                        )}
                        {!hasVideo(music) && isInstrumentalMusic(music) && (
                          <img
                            src="/icons/mcd-s.png"
                            alt={t('musicList.categoryInstrumental', '纯音乐')}
                            className="w-4 h-4 flex-shrink-0 object-contain"
                            title={t('musicList.categoryInstrumental', '纯音乐')}
                          />
                        )}
                        <span className="truncate">{getCurrentLanguageTitle(music)}</span>
                      </h3>
                      <button
                        type="button"
                        className={buttonClass({ variant: 'outline', size: 'xs' })}
                        onClick={(e) => handleOpenDetail(music, e)}
                        title={t('musicDetail.viewDetails', '查看详情')}
                      >
                        {t('musicDetail.viewDetails', '详情')}
                      </button>
                    </div>
                    {music.album && (
                      <p className="text-xs text-white/70 truncate">
                        {music.album}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-white/55">
                      <span>{formatDuration(music.duration)}</span>
                      {type === 'play' && (
                        <span>{t('musicRankings.playCount', { count: music.play_count || 0 })}</span>
                      )}
                      {type === 'favorite' && (
                        <span>{t('musicRankings.favoriteCount', { count: music.favoritesCount || 0 })}</span>
                      )}
                      {type === 'download' && (
                        <span>{t('musicRankings.downloadCount', { count: music.download_count || 0 })}</span>
                      )}
                      {type === 'new' && (
                        <span>
                          {new Date(music.created_at).toLocaleDateString(
                            (i18n?.language === 'tw') ? 'zh\u002dTW' : 'zh\u002dCN'
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MusicRankings;

